import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { StatusDefinition, StatusDomain } from '../types';
import { statusService } from '../services/statusService';
import { useAuth } from './AuthContext';

interface StatusContextValue {
  carerStatuses: StatusDefinition[];
  familyStatuses: StatusDefinition[];
  /** Ordered status definitions for a domain. */
  statusesFor: (domain: StatusDomain) => StatusDefinition[];
  /** Display label for a stored status key (falls back to the raw key). */
  labelFor: (domain: StatusDomain, key?: string | null) => string;
  /** Pill color for a stored status key (falls back to a neutral grey). */
  colorFor: (domain: StatusDomain, key?: string | null) => string;
  loading: boolean;
  /** Reload both domains from the backend (call after editing statuses). */
  refresh: () => Promise<void>;
}

const FALLBACK_COLOR = '#64748b';

const StatusContext = createContext<StatusContextValue | undefined>(undefined);

export const StatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [carerStatuses, setCarerStatuses] = useState<StatusDefinition[]>([]);
  const [familyStatuses, setFamilyStatuses] = useState<StatusDefinition[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const [carers, families] = await Promise.all([
        statusService.getAll('CARER'),
        statusService.getAll('FAMILY'),
      ]);
      setCarerStatuses(carers);
      setFamilyStatuses(families);
    } catch {
      // Leave existing data on transient errors; pages fall back to raw keys.
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setCarerStatuses([]);
      setFamilyStatuses([]);
    }
  }, [isAuthenticated, refresh]);

  const statusesFor = useCallback(
    (domain: StatusDomain) => (domain === 'CARER' ? carerStatuses : familyStatuses),
    [carerStatuses, familyStatuses]
  );

  const labelFor = useCallback(
    (domain: StatusDomain, key?: string | null) => {
      if (!key) return '';
      return statusesFor(domain).find((s) => s.statusKey === key)?.label ?? key;
    },
    [statusesFor]
  );

  const colorFor = useCallback(
    (domain: StatusDomain, key?: string | null) => {
      if (!key) return FALLBACK_COLOR;
      return statusesFor(domain).find((s) => s.statusKey === key)?.color ?? FALLBACK_COLOR;
    },
    [statusesFor]
  );

  return (
    <StatusContext.Provider
      value={{ carerStatuses, familyStatuses, statusesFor, labelFor, colorFor, loading, refresh }}
    >
      {children}
    </StatusContext.Provider>
  );
};

export const useStatuses = (): StatusContextValue => {
  const ctx = useContext(StatusContext);
  if (!ctx) throw new Error('useStatuses must be used within a StatusProvider');
  return ctx;
};
